import React, { useEffect, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import { ProxiedRoutes, Routes } from "@lifeat/shared/system/RouteConstants";
import { PRO_SLACK_COMMUNITY_INVITE_LINK } from "@lifeat/shared/user/UserConstants";
import { getUserSubscriptionTier } from "@lifeat/shared/user/UserUtil";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import LifeAtButton, { LifeAtButtonProps } from "@common/LifeAtButton/LifeAtButton";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import LifeAtDropdownMenu from "@common/LifeAtDropdownMenu/LifeAtDropdownMenu";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import useLocalStorage from "@hooks/useLocalStorage";
import UpgradeIcon from "@icons/Pro/UpgradeLightningBoltIcon.svg";
import UserMenuIcon from "@resources/icons/User-Menu-Icon.svg";
import InstagramIcon from "@resources/logos/social/Instagram.svg";
import TikTokIcon from "@resources/logos/social/TikTok.svg";
import TwitterIcon from "@resources/logos/social/Twitter.svg";
import { LocalStorageKeys } from "@service/LocalStorageService";
import Styles from "@themes/Styles";
import UserAccountModal from "@user/components/UserAccountModal/UserAccountModal";
import AppearanceSettingsModal from "@user/components/UserSettings/AppearanceSettingsModal";
import { buildRoute } from "@util/RouteUtil";
import MenubarCtaModal from "../../../../features/desktop-app/components/MenubarCtaModal";

// Constants for menu elements
const PRO_SLACK_COMMUNITY_BTN_LABEL = "Pro Users Slack";
const HELP_LABEL = "😅 Help";
const HELP_LINK = "https://lifeat.gitbook.io/lifeat";
const FEEDBACK_LABEL = "💡 Feedback";
const BUG_REPORT_LABEL = "🐞 Bug report";
const DOWNLOAD_DESKTOP_APP_LABEL = "Download desktop app";

const SOCIALS_INFO = {
    discord: "https://discord.gg/VAxgkH42yx",
    email: "mailto:contact@lifeat.io",
    instagram: "https://www.instagram.com/lifeat",
    tiktok: "https://www.tiktok.com/@lifeat",
    twitter: "https://www.twitter.com/lifeatspaces/",
};

const StyledUserMenuIcon = styled(UserMenuIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledInstagramIcon = styled(InstagramIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledTwitterIcon = styled(TwitterIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledTikTokIcon = styled(TikTokIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledLifeAtDropdownMenu = styled(LifeAtDropdownMenu)`
    right: ${Styles.distanceFromSidesOfWindow};
    box-shadow: ${(props) => props.theme.boxShadow} ${(props) => props.theme.boxShadowColor};
`;

const StyledLifeAtButton = styled(LifeAtButton)`
    width: unset;
`;

export default function MainDropdownMenu() {
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { userData }: UserContextType = useUserContext();

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const [hasSeenCta, setHasSeenCta] = useLocalStorage<boolean>(LocalStorageKeys.HasUserAcknowledgedMenubarCta, false);
    useEffect(() => {
        // Open the dropdown menu so that the UserAccountModal can render and auto-open.
        if ((userData && !userData.userType) || (window.todesktop && !hasSeenCta)) {
            setDropdownOpen(true);
        }
    }, [userData]);

    // Our custom defined buttons as part of this "main menu" dropdown.
    const menuItemsInfo: LifeAtButtonProps[] = [
        { text: FEEDBACK_LABEL, isLink: true, link: "https://tally.so/r/wogvZx" },
        { text: BUG_REPORT_LABEL, isLink: true, link: "https://tally.so/r/3EAMGr" },
        { text: HELP_LABEL, isLink: true, link: HELP_LINK },
    ];

    const isProUser = useRef(null);
    useEffect(() => {
        if (userData && isProUser.current === undefined) {
            isProUser.current = getUserSubscriptionTier(userData) === "pro";
        }
    }, [userData]);

    if (isProUser.current) {
        menuItemsInfo.unshift({
            text: PRO_SLACK_COMMUNITY_BTN_LABEL,
            icon: <UpgradeIcon className={`social-link-icon ${lightMode ? "light" : "dark"}`} />,
            isLink: true,
            link: PRO_SLACK_COMMUNITY_INVITE_LINK,
        });
    }

    {
        /* Only show desktop app button if not tablet or mobile, and they aren't already in the desktop app */
    }
    if (!window.todesktop && !isMobile && !isTablet) {
        menuItemsInfo.push({ text: DOWNLOAD_DESKTOP_APP_LABEL, isLink: true, link: ProxiedRoutes.Download });
    }

    const menuElements: JSX.Element[] = menuItemsInfo.map((menuItemInfo: LifeAtButtonProps) => {
        return (
            <span
                key={`main-menu-button-${menuItemInfo.text}`}
                className={`dropdown-menu-button-wrapper ${lightMode ? "light" : "dark"}`}
            >
                <LifeAtButton {...menuItemInfo} />
            </span>
        );
    });

    // The social media icons are each a button, but get displayed as 1 row in the dropdown.
    const socialsInfo: LifeAtButtonProps[] = [
        {
            icon: <StyledInstagramIcon className={`social-link-icon`} />,
            isLink: true,
            link: SOCIALS_INFO.instagram,
        },
        {
            icon: <StyledTwitterIcon className={`social-link-icon`} />,
            isLink: true,
            link: SOCIALS_INFO.twitter,
        },
        {
            icon: <StyledTikTokIcon className={`social-link-icon`} />,
            isLink: true,
            link: SOCIALS_INFO.tiktok,
        },
    ];
    const socialLinksButton = (
        <span
            key={`main-menu-button-social-links-buttons`}
            className={`dropdown-menu-button-wrapper ${lightMode ? "light" : "dark"}`}
        >
            {socialsInfo.map((socialInfo: LifeAtButtonProps) => {
                return <StyledLifeAtButton key={`main-menu-button-social-${socialInfo.link}`} {...socialInfo} />;
            })}
        </span>
    );

    // Account info/login/logout buttons should be at the top of the dropdown
    const userAccountButtons = (
        <span
            key={`main-menu-button-user-account-buttons`}
            className={`dropdown-menu-button-wrapper ${lightMode ? "light" : "dark"}`}
        >
            <UserAccountModal forceOpenModal={userData && !userData?.userType} buttonInDropdown={true} />
            {window.todesktop && userData && (
                <MenubarCtaModal
                    onContinueCb={() => {
                        setHasSeenCta(true);
                        setDropdownOpen(false);
                    }}
                />
            )}
        </span>
    );

    const appearanceSettings = (
        <span
            key={`main-menu-app-appearance-settings`}
            className={`dropdown-menu-button-wrapper ${lightMode ? "light" : "dark"}`}
        >
            <AppearanceSettingsModal />
        </span>
    );

    const ALL_MENU_ELEMENTS = [userAccountButtons, appearanceSettings, ...menuElements, socialLinksButton];

    const mainMenuIcon = <StyledUserMenuIcon className={`main-menu-icon`} />;

    return (
        <span>
            <LifeAtButtonV2 size={"small"} onClick={() => setDropdownOpen(!dropdownOpen)}>
                {mainMenuIcon}
                <CaretIconToggle showOpen={dropdownOpen} />
            </LifeAtButtonV2>

            <StyledLifeAtDropdownMenu isOpen={dropdownOpen} onClose={() => setDropdownOpen(false)}>
                {ALL_MENU_ELEMENTS}
            </StyledLifeAtDropdownMenu>
        </span>
    );
}
